<template>
  <div class="onlyoffice-container" :style="{ '--editor-min-height': height }">
    <div v-if="loading" class="text-center pa-4">
      <v-progress-circular
        :size="50"
        :width="5"
        color="#524D4D"
        indeterminate
      ></v-progress-circular>
      <p class="mt-2">Cargando editor...</p>
    </div>
    <div v-if="error" class="pa-4">
      <v-alert type="error" outlined>
        {{ error }}
      </v-alert>
      <v-btn @click="loadEditor" color="#524D4D" dark class="mt-2">
        <v-icon left>mdi-refresh</v-icon>
        Reintentar
      </v-btn>
    </div>
		<div 
			id="main-editor-onlyoffice-ta" 
			class="editor-wrapper" 
			:style="{ display: loading ? 'none' : 'block' }"
			ref="editorWrapper"
			@wheel.passive="onEditorWrapperWheel"
		>
			<div :id="editorId" :style="`width: 100%; height: ${height};`"></div>
			<div class="scroll-interceptor" ref="scrollInterceptor"></div>
		</div>
  </div>
</template>

<script>
import {getEditorConfig} from '@/helpers/api/reports'

export default {
  name: 'OnlyOfficeEditor',
  props: {
    documentId: {
      type: String,
      required: true
    },
    height: {
      type: String,
      default: '600px'
    },
    mode: {
      type: String,
      default: 'edit', // 'edit' o 'view'
      validator: value => ['edit', 'view'].includes(value)
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      editorId: `onlyoffice-editor-${Date.now()}`,
      docEditor: null,
			docConnector: null
    }
  },
  watch: {
    documentId(newId, oldId) {
      if (newId !== oldId) {
        this.destroyEditor()
        this.loadEditor()
      }
    }
  },
  mounted() {
    this.loadOnlyOfficeScript()

    this._iframeWheelHandler = (event) => {
        // Verificar si el mouse está sobre el iframe de OnlyOffice
        const iframe = this.$el.querySelector('iframe')
        if (iframe) {
            const rect = iframe.getBoundingClientRect()
            const isOverIframe = (
                event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom
            )
            // Si el mouse está sobre el iframe, no hacer nada
            // dejamos que el iframe maneje su propio scroll
            if (isOverIframe) return
        }

        // Solo redirigir si el mouse está FUERA del iframe
        let parent = this.$el.parentElement
        while (parent) {
            const style = window.getComputedStyle(parent)
            const overflow = style.overflow + style.overflowY
            if (/(auto|scroll)/.test(overflow) && parent.scrollHeight > parent.clientHeight) {
                parent.scrollTop += event.deltaY
                break
            }
            parent = parent.parentElement
        }
    }

    window.addEventListener('wheel', this._iframeWheelHandler, { passive: true })
  },
  beforeDestroy() {
    this.destroyEditor()
		if (this.$refs.editorWrapper) {
			this.$refs.editorWrapper.removeEventListener('wheel', this._handleWrapperWheel)
		}
  },
  methods: {
		onEditorWrapperWheel(event) {
			// Busca el contenedor padre scrolleable más cercano fuera del editor
			let parent = this.$el.parentElement
			while (parent) {
					const style = window.getComputedStyle(parent)
					const overflow = style.overflow + style.overflowY
					if (/(auto|scroll)/.test(overflow) && parent.scrollHeight > parent.clientHeight) {
							parent.scrollTop += event.deltaY
							break
					}
					parent = parent.parentElement
			}
		},
		reloadEditor() {
			this.destroyEditor()
			this.loadEditor()
		},
    loadOnlyOfficeScript() {
      // Verificar si el script de OnlyOffice ya está cargado
      if (window.DocsAPI) {
        this.loadEditor()
        return
      }

      // Cargar el script de OnlyOffice
      const script = document.createElement('script')
      script.src = 'https://onlyoffice.insomnia.cl/web-apps/apps/api/documents/api.js'
      script.onload = () => {
        console.log('OnlyOffice API script cargado')
        this.loadEditor()
      }
      script.onerror = () => {
        this.error = 'Error al cargar el script de OnlyOffice'
        this.loading = false
      }
      document.head.appendChild(script)
    },
		async saveDocument(){
			console.log('Guardando documento OnlyOffice...')
			this.docEditor.refreshFile()
			// this.docEditor.destroyEditor()
			// this.docEditor = null
			// const config = await this.getEditorConfig()
			// this.docEditor = new window.DocsAPI.DocEditor(this.editorId, config)
		},
    async loadEditor() {
      try {
        this.loading = true
        this.error = null
        
        // Verificar que DocsAPI esté disponible
        if (!window.DocsAPI) {
          throw new Error('DocsAPI no está disponible')
        }
        
        // Esperar a que el DOM esté listo ANTES de obtener la config
        await this.$nextTick()
        
        // Verificar que el elemento del DOM existe
        const editorElement = document.getElementById(this.editorId)
        if (!editorElement) {
          throw new Error('Elemento del editor no encontrado en el DOM')
        }
        
        // Obtener la configuración del editor desde el backend
        // let config = await this.getEditorConfig()
				const editor_config = await getEditorConfig({ document_id: this.documentId })
				if(editor_config.code == 200){
					let config = editor_config.config
					config = {
						...config,
						events: {
							...config.events,
							onDocumentStateChange: (event) => {
								this.$emit('documentStateChange', event);
							},
							onPluginEvent: (event) => {
								console.log('🔵 onPluginEvent recibido:', event);
							},
						}
					}
					console.log('Config: ', config)
					
					// Inicializar el editor de OnlyOffice
					this.docEditor = new window.DocsAPI.DocEditor(this.editorId, config)
					this.loading = false
					this.$emit('loaded', config)
					this._setupScrollOverlay()
				}
      } catch (error) {
        console.error('Error loading OnlyOffice editor:', error)
        this.error = error.response?.data?.error || error.message || 'Error al cargar el editor de documentos'
        this.loading = false
        this.$emit('error', error)
      }
    },

		_setupScrollOverlay() {
				// Crear un overlay absolutamente posicionado sobre el iframe
				// que detecta wheel events y los reenvía al padre,
				// pero SOLO cuando el scroll del iframe ya llegó al límite
				const wrapper = this.$refs.editorWrapper
				if (!wrapper) return

				// El overlay cubre todo pero tiene pointer-events: none por defecto
				// Solo activamos pointer-events temporalmente durante wheel para capturar
				// Mejor: escuchar en el wrapper directamente
				wrapper.addEventListener('wheel', this._handleWrapperWheel, { passive: false })
		},

		_handleWrapperWheel(event) {
				// Este evento viene del documento padre (no del iframe)
				// Solo ocurre si el mouse está sobre el wrapper pero NO dentro del iframe
				// (el iframe bloquea estos eventos)
				const scrollParent = this._findScrollParent(this.$el)
				if (scrollParent) {
						scrollParent.scrollTop += event.deltaY
				}
		},

		_findScrollParent(el) {
				let parent = el.parentElement
				while (parent) {
						const style = window.getComputedStyle(parent)
						const overflow = style.overflow + style.overflowY
						if (/(auto|scroll)/.test(overflow) && parent.scrollHeight > parent.clientHeight) {
								return parent
						}
						parent = parent.parentElement
				}
				return document.documentElement
		},
    
    destroyEditor() {
      if (this.docEditor) {
        try {
          this.docEditor.destroyEditor()
        } catch (e) {
          console.warn('Error al destruir editor:', e)
        }
        this.docEditor = null
      }
    },
    
    async getEditorConfig() {
      try {
				const editor_config = await getEditorConfig({ docId: this.documentId })
				return editor_config
      } catch (error) {
        console.error('Error getting editor config:', error)
        
        if (error.response) {
          throw new Error(error.response.data.error || 'Error del servidor al obtener la configuración del editor')
        } else if (error.request) {
          throw new Error('No se pudo conectar con el servidor')
        } else {
          throw error
        }
      }
    }
  }
}
</script>

<style scoped>
.onlyoffice-container {
  width: 100%;
  height: 100%;
  min-height: var(--editor-min-height);
  position: relative;
}

.editor-wrapper {
  width: 100%;
  height: 100%;
  min-height: var(--editor-min-height);
}

.editor-wrapper iframe {
  min-height: var(--editor-min-height);
  display: block;
}
.scroll-interceptor {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    /* Solo cubre la barra de herramientas superior del editor (~100px)
       donde no hay contenido scrolleable del documento */
    height: 100px;
    z-index: 10;
    pointer-events: none; /* no bloquea clicks, solo reenvía wheel */
}
</style>
<style>
.onlyoffice-container .editor-wrapper iframe {
	min-height: var(--editor-min-height);
}

</style>